import { useState, useEffect } from "react";
import { useVersionedApi } from "../utils/versionedApi";
import { useEscapeKeyAdvanced } from "../hooks/useEscapeKey.jsx";

export default function ChangePasswordModal({ open, onClose, token }) {
  const apiClient = useVersionedApi('v1');
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Check if form has unsaved changes
  const hasUnsavedChanges = () => {
    return form.currentPassword !== '' || 
           form.newPassword !== '' || 
           form.confirmPassword !== '';
  };

  // Esc key functionality with unsaved changes detection
  useEscapeKeyAdvanced(
    open,
    onClose,
    hasUnsavedChanges,
    'You have unsaved changes. Are you sure you want to close this form?'
  );

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      setError("");
      setSuccess("");
      setLoading(false);
    }
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError(""); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.currentPassword) {
      setError("Current password is required");
      return;
    }
    
    if (!form.newPassword) {
      setError("New password is required");
      return;
    }
    
    if (form.newPassword.length < 6) {
      setError("New password must be at least 6 characters");
      return;
    }
    
    if (form.newPassword !== form.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await apiClient.patch(
        `/users/me/password`,
        {
          currentPassword: form.currentPassword,
          newPassword: form.newPassword,
        }
      );

      setSuccess("✅ Password updated successfully!");
      setForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setSuccess("");
        onClose();
      }, 2000);

    } catch (err) {
      console.error("Password update failed:", err);
      setError(err.response?.data?.error || "❌ Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    setError("");
    setSuccess("");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md">
        <div className="pl-5 pr-10 py-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-8">Change Password</h2>
        
          <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Current Password *
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={form.currentPassword}
                onChange={handleChange}
                className="w-full max-w-full box-border border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wecare-primary focus:border-transparent"
                required
                autoComplete="current-password"
              />
            </div>
            
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                New Password *
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={form.newPassword}
                onChange={handleChange}
                className="w-full max-w-full box-border border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wecare-primary focus:border-transparent"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <p className="text-xs text-gray-500 mt-1">Must be at least 6 characters</p>
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm New Password *
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={form.confirmPassword}
                onChange={handleChange}
                className="w-full max-w-full box-border border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-wecare-primary focus:border-transparent"
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {success && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
              <p className="text-sm text-green-600">{success}</p>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-wecare-primary-darker border border-transparent rounded-md hover:bg-wecare-primary-dark focus:outline-none focus:ring-2 focus:ring-wecare-primary disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
}
